import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { ServicesService } from "@core/services/services.service";
import { GenericComponent } from "@shared/components/generic.component";
import { LoadingDrivebService } from "@shared/components/loading-driveb/loading-driveb.service";
import { ServiceDTO } from "@shared/models/services-dto";
import {
  debounceTime,
  first,
  map,
  switchMap,
  takeUntil,
  tap
} from "rxjs/operators";
import { SearchFormDTO } from "./models/search-form-dto";
import { Observable } from "rxjs";
import { PageManager } from "@shared/models/page-manager";
import { DEFAULT_PAGINATION } from "@shared/constants/default-pagination";
import { MatSort } from "@angular/material/sort";
import { MatOptionSelectionChange } from "@angular/material/core";

@Component({
  selector: "app-driveb-services-auto-complete",
  templateUrl: "./services-auto-complete.component.html",
  styleUrls: ["./services-auto-complete.component.scss"],
  standalone: false
})
export class ServicesAutoCompleteComponent
  extends GenericComponent
  implements OnInit
{
  loading = false;
  name = new UntypedFormControl();
  _disabled = false;

  searchForm = this.fb.group({
    name: this.name
  });

  services: ServiceDTO[] = [];

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @Input() page = "";
  @Input() title?: string;
  @Output() onChange: any = new EventEmitter<ServiceDTO>();
  @Input() set required(value: boolean) {
    if (value && !this._disabled) {
      this.searchForm.get("name").setValidators([Validators.required]);
    }
  }
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.searchForm.disable();
    } else {
      this.searchForm.enable();
    }
  }

  constructor(
    private servicesService: ServicesService,
    injector: Injector,
    private loadingDrivebService: LoadingDrivebService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.getServices();
  }

  getServices() {
    this.name.valueChanges
      .pipe(
        debounceTime(200),
        tap(
          () => (
            (this.loading = true), this.loadingDrivebService.setAutoMode(false)
          )
        ),
        switchMap((value: string) => {
          const searchFormDTO: SearchFormDTO = this.searchForm.getRawValue();
          searchFormDTO.name = value;
          return this.servicesService
            .getAllServicesByFilters(
              searchFormDTO,
              this.pageManager,
              this.getSortString()
            )
            .pipe(takeUntil(this.componentDestroyed$));
        })
      )
      .subscribe((observableServices) => {
        observableServices
          .pipe(
            first(),
            map((data) => this.returnData(data))
          )
          .subscribe(
            (data) => {
              this.services = data;
              this.loading = false;
              this.loadingDrivebService.setAutoMode(true);
            },
            (error) => {
              this.loading = false;
              this.loadingDrivebService.setAutoMode(true);
            }
          );
      });
  }

  returnData(data) {
    this.setPageTotalElements(data);
    return data.content;
  }

  setPageTotalElements(data) {
    this.pageManager.totalElements = data.totalElements;
  }

  handleServiceChange(event: MatOptionSelectionChange, service: ServiceDTO) {
    if (event.isUserInput === true) {
      this.onChange.emit(service);
    }
  }

  clean(): void {
    this.searchForm.reset();
    this.services = [];
  }

  setValue(value: string): void {
    this.searchForm.get("name").setValue(value);
    this.services = [];
  }

  pageManager: PageManager = {
    totalElements: 0,
    pageIndex: DEFAULT_PAGINATION.PAGE_INDEX,
    pageSize: DEFAULT_PAGINATION.PAGE_SIZE
  };

  getSortString(): string {
    return "&sort=name,id,asc";
  }
}
